import prizeConfig from '@/data/prizes.json'

export class PrizeManager {
  /**
   * Get available prizes for a specific category
   * @param {string} category - 'short', 'medium', or 'long'
   * @returns {Array} Array of prize configurations
   */
  static getPrizesForCategory(category) {
    return prizeConfig.monthlyPrizes[category] || []
  }

  /**
   * Select a random prize based on weighted probability
   * @param {string} category - Prize category
   * @returns {Object} Selected prize configuration
   */
  static selectRandomPrize(category) {
    const prizes = this.getPrizesForCategory(category)
    if (prizes.length === 0) return null

    const totalWeight = prizes.reduce((sum, prize) => sum + prize.weight, 0)
    let randomValue = Math.random() * totalWeight
    
    for (const prize of prizes) {
      randomValue -= prize.weight
      if (randomValue <= 0) {
        return this.createPrizeInstance(prize)
      }
    }
    
    // Fallback to first prize
    return this.createPrizeInstance(prizes[0])
  }

  /**
   * Create an actual prize instance with unique ID
   * @param {Object} prizeConfig - Prize configuration
   * @returns {Object} Prize instance
   */
  static createPrizeInstance(prizeConfig) {
    return {
      ...prizeConfig,
      instanceId: crypto.randomUUID(),
      dateAwarded: new Date().toISOString(),
      isUsed: false
    }
  }

  /**
   * Get prize rarity color for UI
   * @param {string} rarity - Prize rarity
   * @returns {string} Color class
   */
  static getRarityColor(rarity) {
    return prizeConfig.rarityColors[rarity] || prizeConfig.rarityColors.common
  }

  /**
   * Get category icon for UI
   * @param {string} category - Prize category
   * @returns {string} Icon emoji
   */
  static getCategoryIcon(category) {
    return prizeConfig.categoryIcons[category] || '📦'
  }

  /**
   * Get specific item icon for UI
   * @param {Object} item - Item object
   * @returns {string} Icon emoji
   */
  static getItemIcon(item) {
    if (prizeConfig.itemIcons && prizeConfig.itemIcons[item.id]) {
      return prizeConfig.itemIcons[item.id]
    }
    return this.getCategoryIcon(item.category)
  }

  /**
   * Check if an item can be used on a chocobo
   * @param {Object} item - Item to check
   * @param {Object} chocobo - Target chocobo
   * @returns {Object} Usage validation result
   */
  static validateItemUsage(item, chocobo) {
    if (item.isUsed && item.type === 'consumable') {
      return { valid: false, reason: 'Item has already been consumed' }
    }

    if (item.type === 'equipment' && item.category === 'charm') {
      if (chocobo.equipment?.charm && chocobo.equipment.charm.instanceId !== item.instanceId) {
        return { valid: false, reason: 'Chocobo already has a charm equipped' }
      }
    }

    // Special validation for Sylkis Greens
    if (item.id === 'sylkis_greens' && chocobo.behaviorId === 1) {
      return { 
        valid: true, 
        reason: 'Chocobo already has max behavior, but will still gain +5 Speed and +5 Stamina' 
      }
    }

    return { valid: true }
  }

  /**
   * Get all consumable items from inventory
   * @param {Array} inventory - Player inventory
   * @returns {Array} Consumable items
   */
  static getConsumableItems(inventory) {
    return inventory.filter(item => 
      item.type === 'consumable' && 
      !item.isUsed &&
      item.effects
    )
  }

  /**
   * Get all equipment items from inventory  
   * @param {Array} inventory - Player inventory
   * @returns {Array} Equipment items
   */
  static getEquipmentItems(inventory) {
    return inventory.filter(item => 
      item.type === 'equipment' && 
      item.effects
    )
  }

  /**
   * Get all trophy items from inventory
   * @param {Array} inventory - Player inventory 
   * @returns {Array} Trophy items
   */
  static getTrophyItems(inventory) {
    return inventory.filter(item => 
      item.type === 'monthly' || 
      (!item.type && item.chocobo) // Legacy trophies
    )
  }
}